"use client";

import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface BackgroundParticlesProps {
  show: boolean;
  count?: number;
}

// --- Types for stars and meteors ---
type ParticleType = 'star' | 'meteor';

interface StarParticle {
  type: 'star';
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  baseOpacity: number;
  opacity: number;
  twinkleSpeed: number;
  twinklePhase: number;
}

interface MeteorParticle {
  type: 'meteor';
  x: number;
  y: number;
  vx: number;
  vy: number;
  length: number;
  width: number;
  opacity: number;
  life: number;
  maxLife: number;
}

type Particle = StarParticle | MeteorParticle;

export function BackgroundParticles({ show, count = 40 }: BackgroundParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgUrl = "/SicioStar.svg";
  const imageRef = useRef<HTMLImageElement | null>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    // Don't run effect if user prefers reduced motion or particles are hidden
    if (prefersReducedMotion || !show) return;
    if (!show || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let prevWidth = window.innerWidth;
    let prevHeight = window.innerHeight;
    const particles: Particle[] = [];
    const meteorChance = 0.012; // chance per frame to spawn a meteor
    const maxMeteors = 1; // reduce count

    // --- Particle creation ---
    const createStar = () => {
      const size = Math.random() * 32 + 12; // 12-44px, more variety
      return {
        type: 'star' as const,
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size,
        baseOpacity: Math.random() * 0.5 + 0.5,
        opacity: 1,
        twinkleSpeed: Math.random() * 0.8 + 0.2, // 0.2-1.0
        twinklePhase: Math.random() * Math.PI * 2,
      };
    };

    const createMeteor = () => {
      // Random angle in radians (e.g. -70° to +70°)
      const angle = (Math.random() - 0.5) * (Math.PI * 1.2); // -108° to +108°
      const speed = Math.random() * 2.5 + 2.5; // 2.5-5 px/frame (slower)
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;
      // Start at a random edge
      let x, y;
      if (Math.abs(angle) < Math.PI / 4) {
        // Mostly horizontal: start at left or right
        x = vx > 0 ? -60 : canvas.width + 60;
        y = Math.random() * canvas.height;
      } else {
        // Mostly vertical: start at top or bottom
        x = Math.random() * canvas.width;
        y = vy > 0 ? -60 : canvas.height + 60;
      }
      return {
        type: 'meteor' as const,
        x,
        y,
        vx,
        vy,
        length: Math.random() * 80 + 80, // 80-160px
        width: Math.random() * 2 + 1.5, // 1.5-3.5px
        opacity: Math.random() * 0.3 + 0.4,
        life: 0,
        maxLife: Math.random() * 30 + 60, // frames
      };
    };

    // --- Setup ---
    const resizeCanvas = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      // Scale particle positions
      const scaleX = newWidth / prevWidth;
      const scaleY = newHeight / prevHeight;
      particles.forEach((p) => {
        p.x *= scaleX;
        p.y *= scaleY;
      });
      canvas.width = newWidth;
      canvas.height = newHeight;
      prevWidth = newWidth;
      prevHeight = newHeight;
    };

    const createParticles = () => {
      particles.length = 0;
      for (let i = 0; i < count; i++) {
        particles.push(createStar());
      }
    };

    // --- SVG image for stars ---
    const img = new window.Image();
    img.src = svgUrl;
    imageRef.current = img;

    // --- Animation ---
    let frame = 0;
    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!imageRef.current || !imageRef.current.complete) return;
      frame++;
      
      // Twinkle and move stars
      for (const particle of particles) {
        if (particle.type === 'star') {
          // Twinkle
          particle.opacity = particle.baseOpacity * (0.7 + 0.3 * Math.sin(frame * particle.twinkleSpeed * 0.04 + particle.twinklePhase));
          // Move
          particle.x += particle.vx;
          particle.y += particle.vy;
          if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
          if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;
          ctx.save();
          ctx.globalAlpha = particle.opacity;
          ctx.drawImage(
            imageRef.current,
            particle.x - particle.size / 2,
            particle.y - particle.size / 2,
            particle.size,
            particle.size
          );
          ctx.restore();
        } else if (particle.type === 'meteor') {
          // Move
          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.life++;
          // Draw meteor as a glowing streak, fade in at start
          ctx.save();
          let fade = 1;
          if (particle.life < 10) {
            fade = particle.life / 10; // fade in over first 10 frames
          } else {
            fade = 1 - particle.life / particle.maxLife;
          }
          ctx.globalAlpha = particle.opacity * Math.max(0, fade);
          // Compute tail position based on velocity
          const norm = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy) || 1;
          const dx = (particle.vx / norm) * particle.length;
          const dy = (particle.vy / norm) * particle.length;
          const grad = ctx.createLinearGradient(
            particle.x, particle.y,
            particle.x - dx, particle.y - dy
          );
          grad.addColorStop(0, 'rgba(255,255,255,0.8)');
          grad.addColorStop(0.2, 'rgba(180,200,255,0.5)');
          grad.addColorStop(1, 'rgba(100,102,241,0)');
          ctx.strokeStyle = grad;
          ctx.lineWidth = particle.width;
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(particle.x - dx, particle.y - dy);
          ctx.stroke();
          ctx.restore();
        }
      }
      // Remove dead meteors
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        if (p.type === 'meteor' && (p.life > p.maxLife || p.x > canvas.width + 100)) {
          particles.splice(i, 1);
        }
      }
    };

    const animate = () => {
      // Maybe spawn a meteor
      if (Math.random() < meteorChance && particles.filter(p => p.type === 'meteor').length < maxMeteors) {
        particles.push(createMeteor());
      }
      drawParticles();
      animationFrameId = requestAnimationFrame(animate);
    };

    // Initial setup
    prevWidth = window.innerWidth;
    prevHeight = window.innerHeight;
    canvas.width = prevWidth;
    canvas.height = prevHeight;
    createParticles();
    animate();

    const handleResize = () => {
      resizeCanvas();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
    // eslint-disable-next-line
  }, [show, count, prefersReducedMotion]);

  // Don't render particles if user prefers reduced motion
  if (prefersReducedMotion || !show) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ background: "transparent" }}
    />
  );
}